import { Injectable } from '@angular/core';
import { RoleClaim } from '../models/role-claim/role-claim';
import { GlobalConstants } from '../constant/constant';
import { RoleClaimScope } from '../enums/role-claim/role-claim-scope.enum';
import { RoleClaimKey } from '../enums/role-claim/role-claim-key.enum';

@Injectable({
  providedIn: 'root'
})
/**
 * Service for retrieving and checking the current user's claims.
 */
export class RoleClaimService {

  constructor() { }

  /**
   * Retrieves the claims for the current user.
   * Claims are currently only stored in localStorage.
   * @returns An array of Claim objects representing the user's claims.
   */
  getClaims(): RoleClaim[] {
    const currentUser = JSON.parse(localStorage.getItem(GlobalConstants.USER_KEY)); //claims are currently only stored in localStorage
    return currentUser?.RoleClaims as RoleClaim[] || [];
  }

  /**
   * Retrieves the role claim scope for a given permission key.
   * @param permissionKey - The permission key to retrieve the role claim scope for.
   * @param requiredValue - (Optional) The required value to match for the claim.
   * @returns The role claim scope associated with the permission key, or `RoleClaimScope.None` if not found.
   */
    getClaimScope(permissionKey: RoleClaimKey, requiredValue?: RoleClaimScope): RoleClaimScope {
      const allClaims = this.getClaims();
      const claim = allClaims.find(
        c => c.key === permissionKey && (requiredValue ? c.value === requiredValue : true)
      );
      return claim ? claim.value : RoleClaimScope.None;
    }

  /**
   * Checks if the given scope is an elevated (i.e., not 'None' or 'Self') role claim scope.
   * @param scope - The role claim scope to check.
   * @returns A boolean indicating whether the scope is elevated or not.
   */
  checkIsElevatedScope(scope: RoleClaimScope): boolean {
    return scope !== RoleClaimScope.None && scope !== RoleClaimScope.Self;
  }

  /**
   * Checks if the current user has permission to verify time records and activities.
   * If the user has approve permission, they also implicitly have verify permission.
   * @returns {boolean} Returns true if the current user has permission to verify time records and activities, otherwise returns false.
   */
  checkHasVerifyPermission(): boolean {
    if (this.checkHasApprovePermission()){
      return true
    }
    const timeRecordVerifyScope = this.getClaimScope(RoleClaimKey.TimeRecord_Verify);
    const activityVerifyScope = this.getClaimScope(RoleClaimKey.Activity_Verify);
    return this.checkIsElevatedScope(timeRecordVerifyScope) && this.checkIsElevatedScope(activityVerifyScope);
  }

  /**
   * Checks if the current user has permission to approve time records and activities.
   * @returns {boolean} Returns true if the current user has permission to approve time records and activities, otherwise returns false.
   */
  checkHasApprovePermission(): boolean {
    const timeRecordApproveScope = this.getClaimScope(RoleClaimKey.TimeRecord_Approve);
    const activityApproveScope = this.getClaimScope(RoleClaimKey.Activity_Approve);
    return this.checkIsElevatedScope(timeRecordApproveScope) && this.checkIsElevatedScope(activityApproveScope);
  }

  /**
   * Checks if the current user has permission to enter block time.
   * @returns {boolean} Returns true if the current user has permission to enter block time, otherwise returns false.
   */
  checkCurrentUserHasManualTimeEntryClaim(): boolean {
    const blockTimeEntryClaim = this.getClaimScope(RoleClaimKey.Timecard_Read);
    return blockTimeEntryClaim !== RoleClaimScope.None;
  }

  /**
   * Checks if the current user is a block time manager.
   * A block time manager is defined as a user who has the "Timecard_Read" claim with a value of "Team".
   * @returns {boolean} Returns true if the current user is a block time manager, otherwise returns false.
   */
  checkCurrentUserIsBlockTimeManager(): boolean {
    const blockTimeEntryClaim = this.getClaimScope(RoleClaimKey.Timecard_Read, RoleClaimScope.Team);
    return blockTimeEntryClaim === RoleClaimScope.Team;
  }


  /**
   * Checks if the current user has permission to enable kiosk mode for forms.
   * @returns {boolean} Returns true if the current user has kiosk mode permission, otherwise returns false.
   */
  checkCurrentUserHasKioskModeClaim() {
    return this.getClaimScope(RoleClaimKey.FormDefinition_UseKioskMode) !== RoleClaimScope.None;
  }

  /**
   * Checks if the current user is a payroll lead.
   * A payroll lead is defined as a user who has the "TimeRecord_Lock" claim with a value of "All".
   * @returns {boolean} Returns true if the current user is a payroll lead, otherwise returns false.
   */
  checkCurrentUserIsPayrollLead(): boolean {
    const timeRecordLockClaim = this.getClaimScope(RoleClaimKey.TimeRecord_Lock, RoleClaimScope.All);
    return timeRecordLockClaim === RoleClaimScope.All;
  }

}
